import type { CUMechanicalJobStore } from "@/store/useCUMechanicalJobStore";

let cachedSections: CUMechanicalJobStore["sections"] | null = null;
let cachedDataForSubmit: Record<string, unknown> = {};

const getDataForSubmit = (sections: CUMechanicalJobStore["sections"]) => {
  if (sections === cachedSections) {
    return cachedDataForSubmit;
  }
  cachedSections = sections;

  const dataForSubmit = Object.entries(sections).reduce((acc: any, [key, section]: any) => {
    if (!section?.data || key === "notes" || key === "strongbacks" || key === "strongbackTypeLocation") return acc;
    Object.assign(acc, section.data);
    return acc;
  }, {});

  const shouldIncludeStrongbacks = sections.strongbacks.addMore === true;
  const hasStrongbacksDecision = sections.strongbacks.isDataFilled;

  if (hasStrongbacksDecision) {
    dataForSubmit.do_you_wish_to_add_strongbacks = shouldIncludeStrongbacks;
  }

  if (shouldIncludeStrongbacks) {
    const strongbackEntries =
      sections.strongbackTypeLocation.data.strongbacks?.filter((sb) => {
        return (
          sb?.type ||
          sb?.distance !== undefined ||
          sb?.flange_nps ||
          sb?.flange_rating ||
          sb?.custom_diameter !== undefined ||
          sb?.flange_num_bolt_holes !== undefined ||
          sb?.flange_stud_size !== undefined ||
          sb?.flange_bolt_circle !== undefined
        );
      }) ?? [];

    dataForSubmit.strongback_material = sections.strongbacks.data;
    dataForSubmit.strongback_type_location = {
      ...sections.strongbackTypeLocation.data,
      strongbacks: strongbackEntries,
    };
  }

  cachedDataForSubmit = dataForSubmit;
  return cachedDataForSubmit;
};

export const selectStateForJob = (state: CUMechanicalJobStore) => {
  const sections = state.sections;
  const requiresSecondMinimumClearance =
    sections.repairType.data.repairType === "tee" && sections.checks.data.do_you_need_minimum_clearance === true;

  const areAllRequiredFieldsFilled =
    sections.jobDetails.isDataFilled &&
    sections.additionalJobDetails.isDataFilled &&
    sections.contactInformation.isDataFilled &&
    sections.designConditions.isDataFilled &&
    sections.componentDetails.isDataFilled &&
    sections.defectDetails.isDataFilled &&
    sections.postConstructionTestingRequirements.isDataFilled &&
    sections.drawingNotes.isDataFilled &&
    sections.checks.isDataFilled &&
    (sections.checks.data.do_you_need_minimum_clearance !== true || sections.checks.data.min_id_clearance != null) &&
    (!requiresSecondMinimumClearance ||
      (sections.checks.data as { min_id_clearance_2?: number | null }).min_id_clearance_2 != null) &&
    (sections.checks.data.do_you_want_to_add_mdmt_to_package !== true || sections.checks.data.mdmt != null) &&
    (sections.checks.data.is_this_revision !== true ||
      (sections.checks.data.revision != null && sections.checks.data.revision > 0)) &&
    (sections.checks.data.do_you_wish_to_change_default_checked_by !== true ||
      (sections.checks.data.checked_by_first_name != null && sections.checks.data.checked_by_last_name != null)) &&
    // Joint efficiency is only required if user chose to change defaults
    (sections.checks.data.do_you_wish_to_change_default_joint_efficiency !== true ||
      sections.jointEfficiency.isDataFilled);

  const dataForSubmit = getDataForSubmit(state.sections);

  const atLeastOneSectionFilled =
    sections.jobDetails.isDataFilled ||
    sections.additionalJobDetails.isDataFilled ||
    sections.contactInformation.isDataFilled ||
    sections.designConditions.isDataFilled ||
    sections.componentDetails.isDataFilled ||
    sections.defectDetails.isDataFilled;

  return {
    areAllRequiredFieldsFilled,
    atLeastOneSectionFilled,
    dataForSubmit,
    ...state,
  };
};
